import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Grid,
	IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import CustomAutocomplete from "../../components/CustomAutocomplete";
import { OrdemProducaoService } from "../../service/ordem_producao_service";
import { ProdutoService } from "../../service/produto_service";
import { ProcessoService } from "../../service/processo_service";
import { EntradaProdutoService } from "../../service/entrada_produto_service";
import { useUser } from "../../context/UserContext";
import { hojeISO } from "../../utils/dateUtils";

// Converte o valor cru de um input numerico para numero (campo vazio = 0).
const num = (valor) => {
	const n = parseFloat(valor);
	return Number.isFinite(n) ? n : 0;
};

// Produto utilizado na etapa (so conta quando a etapa solicita produto).
const produtoDaEtapa = (etapa) => (etapa.solicitaProduto ? num(etapa.quantidadeProduto) : 0);

// Na tela, quantidadeSaida e o que o usuario digita: so a parte que veio da
// entrada, limitada a ela. O produto utilizado e considerado todo aproveitado
// e somado automaticamente — essa soma e a saida real da etapa, que vira a
// entrada da proxima e e o que se grava. Ex.: entrada 20, produto 50, digitado
// 20 -> perdas 0 e saida total 70.
const saidaTotalDaEtapa = (etapa) => num(etapa.quantidadeSaida) + produtoDaEtapa(etapa);

// Reaplica a cadeia a partir de uma etapa: entrada de cada etapa seguinte e a
// saida total da anterior, e as perdas sao a entrada menos o digitado.
const propagarEtapas = (etapas, aPartirDe = 0) => {
	for (let i = aPartirDe; i < etapas.length; i++) {
		if (i > 0) {
			etapas[i].quantidadeEntrada = saidaTotalDaEtapa(etapas[i - 1]);
		}
		etapas[i].perdas = num(etapas[i].quantidadeEntrada) - num(etapas[i].quantidadeSaida);
	}
	return etapas;
};

// Descricao de uma OP de origem usada como produto em uma etapa.
const descreverOrigem = (ordem) => {
	if (!ordem) return "";
	const cultivo = ordem.produtoFinal?.cultivo;
	const cultura = cultivo?.cultura || "Sem cultura";
	const variedade = cultivo?.variedade ? ` - ${cultivo.variedade}` : "";
	const lote = ordem.loteRastreabilidade ? ` | Lote ${ordem.loteRastreabilidade}` : "";
	return `OP ${ordem.numeroOP} - ${cultura}${variedade}${lote}`;
};

// Ordena produtos por "cultura variedade" no padrao pt-BR.
const sortProdutos = (a, b) => {
	const nomeA = `${a.cultivo?.cultura || ""} ${a.cultivo?.variedade || ""}`.toLowerCase();
	const nomeB = `${b.cultivo?.cultura || ""} ${b.cultivo?.variedade || ""}`.toLowerCase();
	return nomeA.localeCompare(nomeB, "pt-BR");
};

function OrdemProducaoCad() {
	const navigate = useNavigate();
	const { id } = useParams();
	const isEdit = Boolean(id);
	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	// userId vem do perfil mantido em memória pelo UserContext.
	const { user } = useUser();
	const userId = user?.userId;

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	// Form data
	const [data, setData] = useState(hojeISO());
	const [produtoEntradaId, setProdutoEntradaId] = useState("");
	const [loteConsolidadoId, setLoteConsolidadoId] = useState("");
	const [quantidadeEntrada, setQuantidadeEntrada] = useState("");
	const [processoId, setProcessoId] = useState("");
	const [produtoFinalId, setProdutoFinalId] = useState("");
	const [embalagemId, setEmbalagemId] = useState("");

	// Lists for selects
	const [produtosEntrada, setProdutosEntrada] = useState([]);
	const [produtosFinais, setProdutosFinais] = useState([]);
	const [processos, setProcessos] = useState([]);
	const [embalagens, setEmbalagens] = useState([]);
	const [lotes, setLotes] = useState([]);

	// Etapas
	const [etapas, setEtapas] = useState([]);

	// OPs anteriores com saldo: sao os produtos que as etapas marcadas como
	// "solicita produto" podem consumir.
	const [produtosDisponiveis, setProdutosDisponiveis] = useState([]);

	// Selected data
	const [selectedProcesso, setSelectedProcesso] = useState(null);
	const [selectedProdutoFinal, setSelectedProdutoFinal] = useState(null);
	const [selectedEmbalagem, setSelectedEmbalagem] = useState(null);
	const [selectedLote, setSelectedLote] = useState(null);

	// Computed values
	const [dadosOP, setDadosOP] = useState(null);

	// Em edicao: lote e quantidade originais da OP. O backend ja baixou essa
	// quantidade do lote, entao ela precisa ser somada de volta ao disponivel
	// para a propria OP poder ser reaberta e re-salvada.
	const [loteOriginalId, setLoteOriginalId] = useState("");
	const [quantidadeEntradaOriginal, setQuantidadeEntradaOriginal] = useState(0);

	const disponivelDoLote = useCallback(
		(lote) => {
			if (!lote) return 0;
			const base = Number(lote.quantidadeDisponivel ?? lote.quantidade ?? 0);
			if (isEdit && lote.id === loteOriginalId) return base + quantidadeEntradaOriginal;
			return base;
		},
		[isEdit, loteOriginalId, quantidadeEntradaOriginal]
	);

	// Saldo da OP de origem descontando o que as OUTRAS etapas desta mesma OP ja
	// reservaram dela — sem isso duas etapas poderiam consumir o mesmo saldo.
	const disponivelParaEtapa = useCallback(
		(indiceEtapa, ordemOrigemId) => {
			if (!ordemOrigemId) return 0;

			const origem = produtosDisponiveis.find((p) => p.id === ordemOrigemId);
			if (!origem) return 0;

			const reservadoPorOutras = etapas.reduce((soma, etapa, indice) => {
				if (indice === indiceEtapa) return soma;
				if (!etapa.solicitaProduto) return soma;
				if (etapa.ordemProducaoProdutoId !== ordemOrigemId) return soma;
				return soma + num(etapa.quantidadeProduto);
			}, 0);

			return num(origem.quantidadeDisponivel) - reservadoPorOutras;
		},
		[etapas, produtosDisponiveis]
	);

	const loadInitialData = useCallback(async () => {
		setLoading(true);
		try {
			const produtoService = new ProdutoService();
			const processoService = new ProcessoService();

			const ordemService = new OrdemProducaoService();

			const [prodEntrada, procs, disponiveis] = await Promise.all([
				produtoService.getAll(unidadeDistribuidoraId, "entrada"),
				processoService.getAll(unidadeDistribuidoraId),
				ordemService.getProdutosDisponiveis(unidadeDistribuidoraId, id),
			]);

			setProdutosEntrada((prodEntrada || []).sort(sortProdutos));
			setProcessos(procs || []);
			setProdutosDisponiveis(disponiveis || []);
		} catch (error) {
			console.error("Erro ao carregar dados:", error);
			alert(error.message || "Erro ao carregar dados");
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, id]);

	// Carrega a OP e TODAS as listas dependentes antes de preencher o formulario.
	// Nao reutiliza os handlers de "change" de proposito: eles limpam os campos
	// filhos (produto final, lote e embalagem) como efeito colateral, o que
	// apagava justamente o que acabara de ser preenchido na edicao.
	const loadOrdemProducao = useCallback(async () => {
		setLoading(true);
		try {
			const ordemService = new OrdemProducaoService();
			const produtoService = new ProdutoService();
			const processoService = new ProcessoService();
			const entradaProdutoService = new EntradaProdutoService();

			const ordem = await ordemService.getById(id);

			const [todosFinais, procs, lotesData, todasEmbalagens] = await Promise.all([
				produtoService.getAll(unidadeDistribuidoraId, "final"),
				processoService.getAll(unidadeDistribuidoraId),
				entradaProdutoService.getLotesConsolidados(unidadeDistribuidoraId, ordem.produtoEntradaId),
				ordem.produtoFinalId ? produtoService.getEmbalagens(ordem.produtoFinalId) : Promise.resolve([]),
			]);

			const finaisOrdenados = (todosFinais || []).sort(sortProdutos);
			setProdutosFinais(finaisOrdenados);
			setProcessos(procs || []);

			// O lote desta OP pode ter sido totalmente consumido por ela mesma e nao
			// voltar mais na lista de disponiveis. Sem reinseri-lo, o campo abria
			// vazio e nao havia como selecionar o lote de novo.
			let listaLotes = lotesData || [];
			let lote = listaLotes.find((l) => l.id === ordem.loteConsolidadoId) || null;
			if (!lote && ordem.loteConsolidadoId) {
				lote = {
					id: ordem.loteConsolidadoId,
					codigoRastreio:
						ordem.loteConsolidado?.codigoRastreio || ordem.loteRastreabilidade || "SEM_RASTREIO",
					quantidade: Number(ordem.loteConsolidado?.quantidade ?? ordem.quantidadeEntrada ?? 0),
					quantidadeDisponivel: Number(ordem.loteConsolidado?.quantidadeDisponivel ?? 0),
					unidadeMedidaId: ordem.loteConsolidado?.unidadeMedidaId,
					unidadeMedida: ordem.loteConsolidado?.unidadeMedida,
				};
				listaLotes = [lote, ...listaLotes];
			}
			setLotes(listaLotes);
			setSelectedLote(lote);
			setLoteConsolidadoId(lote?.id || "");
			setLoteOriginalId(ordem.loteConsolidadoId || "");
			setQuantidadeEntradaOriginal(Number(ordem.quantidadeEntrada) || 0);

			// Embalagens do produto final filtradas pela unidade de medida do lote.
			// A embalagem ja gravada entra na lista mesmo se o filtro a excluir,
			// senao o Select ficaria com um valor sem opcao correspondente.
			let embalagensFiltradas = (todasEmbalagens || []).filter(
				(emb) => !lote?.unidadeMedidaId || emb.unidadeMedidaId === lote.unidadeMedidaId
			);
			const embalagemSalva = (todasEmbalagens || []).find((e) => e.id === ordem.embalagemProdutoFinalId);
			if (embalagemSalva && !embalagensFiltradas.some((e) => e.id === embalagemSalva.id)) {
				embalagensFiltradas = [embalagemSalva, ...embalagensFiltradas];
			}
			setEmbalagens(embalagensFiltradas);
			setEmbalagemId(ordem.embalagemProdutoFinalId || "");
			setSelectedEmbalagem(embalagemSalva || null);

			setData(ordem.data.split("T")[0]);
			setProdutoEntradaId(ordem.produtoEntradaId);
			setQuantidadeEntrada(ordem.quantidadeEntrada.toString());
			setProcessoId(ordem.processoId);
			setSelectedProcesso((procs || []).find((p) => p.id === ordem.processoId) || null);
			setProdutoFinalId(ordem.produtoFinalId);
			setSelectedProdutoFinal(finaisOrdenados.find((p) => p.id === ordem.produtoFinalId) || null);

			// Etapas gravadas: preservadas como vieram, sem passar pela
			// reinicializacao zerada de handleProcessoChange. Os campos de produto
			// viram string vazia quando nulos para os inputs ficarem controlados.
			// A saida gravada e a total (digitado + produto); na tela o campo
			// mostra so a parte digitada.
			setEtapas(
				(ordem.etapas || []).map((etapa) => ({
					...etapa,
					quantidadeSaida: etapa.solicitaProduto
						? num(etapa.quantidadeSaida) - num(etapa.quantidadeProduto)
						: etapa.quantidadeSaida,
					solicitaProduto: Boolean(etapa.solicitaProduto),
					ordemProducaoProdutoId: etapa.ordemProducaoProdutoId || "",
					quantidadeProduto:
						etapa.quantidadeProduto === null || etapa.quantidadeProduto === undefined
							? ""
							: etapa.quantidadeProduto,
				}))
			);
		} catch (error) {
			console.error("Erro ao carregar ordem:", error);
			alert(error.message || "Erro ao carregar ordem de produção");
		} finally {
			setLoading(false);
		}
	}, [id, unidadeDistribuidoraId]);

	const handleProdutoEntradaChange = async (value) => {
		setProdutoEntradaId(value);

		if (value) {
			try {
				const produtoService = new ProdutoService();
				const entradaProdutoService = new EntradaProdutoService();

				// Carregar produtos finais
				const todosFinais = await produtoService.getAll(unidadeDistribuidoraId, "final");
				setProdutosFinais((todosFinais || []).sort(sortProdutos));

				// Carregar lotes consolidados do produto selecionado
				const lotesData = await entradaProdutoService.getLotesConsolidados(unidadeDistribuidoraId, value);
				setLotes(lotesData || []);
			} catch (error) {
				console.error("Erro ao carregar dados do produto:", error);
			}
		} else {
			setProdutosFinais([]);
			setLotes([]);
		}

		setProdutoFinalId("");
		setEmbalagemId("");
		setEmbalagens([]);
		setLoteConsolidadoId("");
	};

	const handleLoteChange = async (newLote) => {
		setLoteConsolidadoId(newLote?.id || "");
		setSelectedLote(newLote);

		// Resetar embalagem ao mudar lote
		setEmbalagemId("");
		setSelectedEmbalagem(null);

		// Se já tem produto final selecionado, recarregar embalagens com novo filtro
		if (produtoFinalId && newLote) {
			try {
				const produtoService = new ProdutoService();
				const embs = await produtoService.getEmbalagens(produtoFinalId);

				// Filtrar embalagens pela mesma unidade de medida do lote consolidado
				let embalagensFiltered = embs || [];
				if (newLote && newLote.unidadeMedidaId) {
					embalagensFiltered = embalagensFiltered.filter(
						emb => emb.unidadeMedidaId === newLote.unidadeMedidaId
					);
				}

				setEmbalagens(embalagensFiltered);
			} catch (error) {
				console.error("Erro ao carregar embalagens:", error);
			}
		} else if (!newLote) {
			// Se não tem lote, limpar embalagens
			setEmbalagens([]);
		}
	};

	const handleProdutoFinalChange = async (value) => {
		setProdutoFinalId(value);

		if (value) {
			try {
				const produtoService = new ProdutoService();
				const embs = await produtoService.getEmbalagens(value);

				// Filtrar embalagens pela mesma unidade de medida do lote consolidado
				let embalagensFiltered = embs || [];
				if (selectedLote && selectedLote.unidadeMedidaId) {
					embalagensFiltered = embalagensFiltered.filter(
						emb => emb.unidadeMedidaId === selectedLote.unidadeMedidaId
					);
				}

				setEmbalagens(embalagensFiltered);

				const produto = produtosFinais.find(p => p.id === value);
				setSelectedProdutoFinal(produto);
			} catch (error) {
				console.error("Erro ao carregar embalagens:", error);
			}
		} else {
			setEmbalagens([]);
			setSelectedProdutoFinal(null);
		}

		setEmbalagemId("");
	};

	const handleEmbalagemChange = (value) => {
		setEmbalagemId(value);
		const emb = embalagens.find(e => e.id === value);
		setSelectedEmbalagem(emb);
	};

	const handleProcessoChange = (value) => {
		setProcessoId(value);
		const proc = processos.find(p => p.id === value);
		setSelectedProcesso(proc);

		if (proc && proc.etapas) {
			// Inicializar etapas
			const qtdEntrada = parseFloat(quantidadeEntrada) || 0;
			// quantidadeSaida nasce vazia (e nao 0) para o campo nao abrir com um
			// zero que o usuario precisa apagar antes de digitar.
			const etapasIniciais = proc.etapas.map((etapa, index) => ({
				numeroEtapa: etapa.posicao,
				nomeEtapa: etapa.nome,
				quantidadeEntrada: index === 0 ? qtdEntrada : 0,
				quantidadeSaida: "",
				perdas: index === 0 ? qtdEntrada : 0,
				// A marcacao vem do cadastro do processo; a origem e a quantidade
				// sao preenchidas pelo usuario na tabela de etapas.
				solicitaProduto: Boolean(etapa.solicitaProduto),
				ordemProducaoProdutoId: "",
				quantidadeProduto: "",
			}));
			setEtapas(etapasIniciais);
		} else {
			setEtapas([]);
		}
	};

	const handleQuantidadeEntradaChange = (value) => {
		setQuantidadeEntrada(value);
		recalcularEtapas(value);
	};

	const recalcularEtapas = (quantEntrada) => {
		if (etapas.length === 0) return;

		const novasEtapas = etapas.map((etapa) => ({ ...etapa }));

		// Primeira etapa tem entrada = quantidade informada
		novasEtapas[0].quantidadeEntrada = num(quantEntrada);

		setEtapas(propagarEtapas(novasEtapas));
	};

	const handleEtapaSaidaChange = (index, value) => {
		// Guarda o valor cru do input. Converter para numero aqui reescrevia o
		// campo vazio como 0, e o zero voltava para a tela a cada tecla.
		const novasEtapas = etapas.map((etapa) => ({ ...etapa }));

		novasEtapas[index].quantidadeSaida = value;
		propagarEtapas(novasEtapas, index);

		setEtapas(novasEtapas);
		atualizarDadosOP(novasEtapas);
	};

	const handleEtapaProdutoChange = (index, ordemOrigem) => {
		const novasEtapas = etapas.map((etapa) => ({ ...etapa }));
		novasEtapas[index].ordemProducaoProdutoId = ordemOrigem?.id || "";
		// Trocar de produto zera a quantidade: o limite e outro.
		novasEtapas[index].quantidadeProduto = "";
		propagarEtapas(novasEtapas, index);
		setEtapas(novasEtapas);
		atualizarDadosOP(novasEtapas);
	};

	// O produto utilizado entra na saida total da etapa, entao mexer nele muda
	// a entrada das etapas seguintes na hora.
	const handleEtapaQuantidadeProdutoChange = (index, value) => {
		const novasEtapas = etapas.map((etapa) => ({ ...etapa }));
		novasEtapas[index].quantidadeProduto = value;
		propagarEtapas(novasEtapas, index);
		setEtapas(novasEtapas);
		atualizarDadosOP(novasEtapas);
	};

	const atualizarDadosOP = useCallback((etapasAtualizadas = etapas) => {
		if (!quantidadeEntrada || etapasAtualizadas.length === 0) {
			setDadosOP(null);
			return;
		}

		const ultimaEtapa = etapasAtualizadas[etapasAtualizadas.length - 1];
		const quantidadeFinal = saidaTotalDaEtapa(ultimaEtapa);
		const qtdEntrada = num(quantidadeEntrada);
		const rendimento = qtdEntrada > 0 ? ((quantidadeFinal / qtdEntrada) * 100).toFixed(2) : 0;

		// Calcular volumes
		const qtdEmbalagem = selectedEmbalagem?.quantidade || 0;
		const volumes = selectedEmbalagem && qtdEmbalagem > 0 ? Math.floor(quantidadeFinal / qtdEmbalagem) : 0;

		const embalagemDescricao = selectedEmbalagem
			? `${selectedEmbalagem.descricao} (${selectedEmbalagem.quantidade} ${selectedEmbalagem.unidadeMedida?.sigla || ""})`
			: "-";

		// Montar descrição do produto final com cultura e variedade
		let produtoFinalDescricao = "-";
		if (selectedProdutoFinal?.cultivo) {
			const cultura = selectedProdutoFinal.cultivo.cultura || "";
			const variedade = selectedProdutoFinal.cultivo.variedade ? ` - ${selectedProdutoFinal.cultivo.variedade}` : "";
			produtoFinalDescricao = `${cultura}${variedade}`;
		}

		setDadosOP({
			numeroOP: isEdit ? "OP " + id : "Nova OP",
			produtoFinal: produtoFinalDescricao,
			quantidadeEntrada: qtdEntrada,
			quantidadeFinal,
			embalagem: embalagemDescricao,
			volumes,
			data: new Date(data).toLocaleDateString("pt-BR", { timeZone: "UTC" }),
			loteRastreabilidade: selectedLote?.codigoRastreio || "-",
			rendimento: rendimento + "%",
		});
	}, [quantidadeEntrada, selectedProdutoFinal, selectedEmbalagem, data, selectedLote, etapas, isEdit, id]);

	useEffect(() => {
		loadInitialData();
	}, [loadInitialData]);

	useEffect(() => {
		if (id && isEdit) {
			loadOrdemProducao();
		}
	}, [id, isEdit, loadOrdemProducao]);

	// Atualizar dadosOP sempre que houver mudanças relevantes
	useEffect(() => {
		atualizarDadosOP();
	}, [atualizarDadosOP]);

	const handleSave = async () => {
		// Validações
		if (!data || !produtoEntradaId || !loteConsolidadoId || !quantidadeEntrada || !processoId || !produtoFinalId) {
			alert("Por favor, preencha todos os campos obrigatórios");
			return;
		}

		if (etapas.length === 0) {
			alert("Nenhuma etapa foi configurada");
			return;
		}

		// Validar quantidade de entrada vs disponível no lote
		if (selectedLote) {
			const qtdDisponivel = disponivelDoLote(selectedLote);
			const qtdEntrada = parseFloat(quantidadeEntrada);

			if (qtdEntrada > qtdDisponivel) {
				alert(`A quantidade de entrada (${qtdEntrada.toFixed(2)}) não pode ser maior que a quantidade disponível no lote (${qtdDisponivel.toFixed(2)})`);
				return;
			}
		}

		// Saída digitada maior que a entrada deixaria as perdas negativas.
		for (const etapa of etapas) {
			const entrada = num(etapa.quantidadeEntrada);
			if (num(etapa.quantidadeSaida) > entrada) {
				alert(
					`A quantidade de saída da etapa ${etapa.numeroEtapa} - ${etapa.nomeEtapa} (${num(
						etapa.quantidadeSaida
					).toFixed(2)}) não pode ser maior que a quantidade de entrada (${entrada.toFixed(2)})`
				);
				return;
			}
		}

		// Etapas que solicitam produto: origem obrigatoria e quantidade dentro do
		// saldo da OP de origem.
		for (let index = 0; index < etapas.length; index++) {
			const etapa = etapas[index];
			if (!etapa.solicitaProduto) continue;

			const identificacao = `${etapa.numeroEtapa} - ${etapa.nomeEtapa}`;

			if (!etapa.ordemProducaoProdutoId) {
				alert(`Informe o produto utilizado na etapa ${identificacao}`);
				return;
			}

			const quantidade = num(etapa.quantidadeProduto);
			if (quantidade <= 0) {
				alert(`Informe a quantidade do produto utilizado na etapa ${identificacao}`);
				return;
			}

			const disponivel = disponivelParaEtapa(index, etapa.ordemProducaoProdutoId);
			if (quantidade > disponivel) {
				alert(
					`A quantidade do produto na etapa ${identificacao} (${quantidade.toFixed(2)}) não pode ser maior que o estoque disponível (${disponivel.toFixed(2)})`
				);
				return;
			}
		}

		setSaving(true);
		try {
			const ordemService = new OrdemProducaoService();

			const payload = {
				id: isEdit ? id : undefined,
				unidadeDistribuidoraId,
				data,
				produtoEntradaId,
				loteConsolidadoId,
				quantidadeEntrada: parseFloat(quantidadeEntrada),
				processoId,
				produtoFinalId,
				embalagemProdutoFinalId: embalagemId || null,
				responsavel_cadastro_id: userId,
				responsavel_atualizacao_id: userId,
				// As etapas carregam o valor cru digitado; o backend recebe numeros.
				etapas: etapas.map((etapa) => ({
					...etapa,
					quantidadeEntrada: num(etapa.quantidadeEntrada),
					quantidadeSaida: saidaTotalDaEtapa(etapa),
					perdas: num(etapa.perdas),
					solicitaProduto: Boolean(etapa.solicitaProduto),
					ordemProducaoProdutoId: etapa.solicitaProduto
						? etapa.ordemProducaoProdutoId || null
						: null,
					quantidadeProduto: etapa.solicitaProduto ? num(etapa.quantidadeProduto) : null,
				})),
			};

			await ordemService.save(payload);
			navigate("/ordem-producao");
		} catch (error) {
			console.error("Erro ao salvar:", error);
			alert(error.message || "Erro ao salvar ordem de produção");
		} finally {
			setSaving(false);
		}
	};

	// As colunas de produto so aparecem quando o processo escolhido tem alguma
	// etapa marcada como "solicita produto" no cadastro.
	const temEtapaComProduto = etapas.some((etapa) => etapa.solicitaProduto);

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "100vh",
				}}
			>
				<Typography>Carregando...</Typography>
			</Box>
		);
	}

	return (
		<AppLayout currentPage="ordem-producao">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Título */}
					<Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton onClick={() => navigate("/ordem-producao")} sx={{ color: "#666" }}>
							<ArrowBackIcon />
						</IconButton>
						<Typography
							variant="h5"
							sx={{
								color: "#666",
								fontWeight: 500,
								fontSize: "20px",
							}}
						>
							{isEdit ? "Editar Ordem de Produção" : "Nova Ordem de Produção"}
						</Typography>
					</Box>

					{/* Container branco */}
					<Box
						sx={{
							bgcolor: "white",
							borderRadius: "4px",
							boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
							overflow: "hidden",
						}}
					>
						{/* Formulário */}
						<Box sx={{ px: 6, py: 4 }}>
							{/* Título da seção */}
							<Typography
								variant="subtitle1"
								sx={{ color: "#1a9e8f", fontWeight: 600, mb: 2, fontSize: "16px" }}
							>
								Dados da Ordem de Produção
							</Typography>

							{/* Linha de campos do formulário */}
							<Grid container spacing={2} sx={{ mb: 3 }}>
								<Grid size={{ xs: 6, sm: 4, md: 2 }}>
									<TextField
										fullWidth
										label="Data"
										type="date"
										value={data}
										onChange={(e) => setData(e.target.value)}
										InputLabelProps={{ style: { fontSize: "15px" }, shrink: true }}
										inputProps={{ style: { fontSize: "15px" } }}
										size="small"
									/>
								</Grid>

								<Grid size={{ xs: 12, sm: 6, md: 3 }}>
									<CustomAutocomplete
										size="small"
										options={produtosEntrada}
										value={produtosEntrada.find(p => p.id === produtoEntradaId) || null}
										onChange={(newValue) => handleProdutoEntradaChange(newValue?.id || "")}
										getOptionLabel={(option) => {
											const cultura = option.cultivo?.cultura || "Sem cultura";
											const variedade = option.cultivo?.variedade ? ` - ${option.cultivo.variedade}` : "";
											return `${cultura}${variedade}`;
										}}
										getOptionId={(option) => option.id}
										noOptionsText="Nenhum produto disponível"
										label="Produto Entrada"
										placeholder="Buscar produto..."
									/>
								</Grid>

								<Grid size={{ xs: 12, sm: 6, md: 3 }}>
									<CustomAutocomplete
										size="small"
										disabled={!produtoEntradaId}
										options={lotes}
										value={lotes.find(l => l.id === loteConsolidadoId) || null}
										onChange={(newValue) => handleLoteChange(newValue)}
										getOptionLabel={(option) => {
											const codigoRastreio = option.codigoRastreio || "SEM_RASTREIO";
											const unidade = option.unidadeMedida?.sigla || "";
											return `${codigoRastreio} - Disponível: ${disponivelDoLote(option).toFixed(2)} ${unidade}`;
										}}
										getOptionId={(option) => option.id}
										noOptionsText="Nenhum lote disponível"
										label="Lote Consolidado"
										placeholder="Selecionar lote..."
									/>
								</Grid>

								<Grid size={{ xs: 6, sm: 4, md: 2 }}>
									<TextField
										fullWidth
										label="Quant. Entrada"
										type="number"
										value={quantidadeEntrada}
										onChange={(e) => handleQuantidadeEntradaChange(e.target.value)}
										InputLabelProps={{ style: { fontSize: "15px" } }}
										inputProps={{ style: { fontSize: "15px" }, min: 0, step: 0.01 }}
										error={Boolean(selectedLote) && parseFloat(quantidadeEntrada) > disponivelDoLote(selectedLote)}
										helperText={
											selectedLote && parseFloat(quantidadeEntrada) > disponivelDoLote(selectedLote)
												? `Máximo: ${disponivelDoLote(selectedLote).toFixed(2)}`
												: ""
										}
										size="small"
									/>
								</Grid>

								<Grid size={{ xs: 6, sm: 4, md: 2 }}>
									<FormControl fullWidth size="small">
										<InputLabel style={{ fontSize: "15px" }}>Processo</InputLabel>
										<Select
											value={processoId}
											onChange={(e) => handleProcessoChange(e.target.value)}
											label="Processo"
											style={{ fontSize: "15px" }}
											displayEmpty
										>
											{processos.length === 0 && (
												<MenuItem disabled style={{ fontSize: "15px" }}>
													Nenhum processo disponível
												</MenuItem>
											)}
											{processos.map((proc) => (
												<MenuItem key={proc.id} value={proc.id} style={{ fontSize: "15px" }}>
													{proc.nome}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								<Grid size={{ xs: 12, sm: 6, md: 3 }}>
									<CustomAutocomplete
										size="small"
										disabled={!produtoEntradaId}
										options={produtosFinais}
										value={produtosFinais.find(p => p.id === produtoFinalId) || null}
										onChange={(newValue) => handleProdutoFinalChange(newValue?.id || "")}
										getOptionLabel={(option) => {
											const cultura = option.cultivo?.cultura || "Sem cultura";
											const variedade = option.cultivo?.variedade ? ` - ${option.cultivo.variedade}` : "";
											return `${cultura}${variedade}`;
										}}
										getOptionId={(option) => option.id}
										noOptionsText="Nenhum produto disponível"
										label="Produto Final"
										placeholder="Buscar produto..."
									/>
								</Grid>

								<Grid size={{ xs: 6, sm: 4, md: 2 }}>
									<FormControl fullWidth size="small" disabled={!produtoFinalId}>
										<InputLabel style={{ fontSize: "15px" }}>Embalagem</InputLabel>
										<Select
											value={embalagemId}
											onChange={(e) => handleEmbalagemChange(e.target.value)}
											label="Embalagem"
											style={{ fontSize: "15px" }}
											displayEmpty
										>
											{embalagens.length === 0 && (
												<MenuItem disabled style={{ fontSize: "15px" }}>
													Nenhuma embalagem disponível
												</MenuItem>
											)}
											{embalagens.map((emb) => {
												const unidade = emb.unidadeMedida?.sigla || "";
												const descricao = unidade
													? `${emb.descricao} (${emb.quantidade} ${unidade})`
													: emb.descricao;
												return (
													<MenuItem key={emb.id} value={emb.id} style={{ fontSize: "15px" }}>
														{descricao}
													</MenuItem>
												);
											})}
										</Select>
									</FormControl>
								</Grid>
							</Grid>

							{/* Fluxo do Processo */}
							{selectedProcesso && (
								<Typography
									variant="subtitle1"
									sx={{
										color: "#1a9e8f",
										fontWeight: 600,
										mb: 2,
										mt: 4,
										fontSize: "16px",
									}}
								>
									Fluxo de {selectedProcesso.nome}
								</Typography>
							)}

							{/* Tabela de Dados da OP */}
							{dadosOP && (
								<Box sx={{ mb: 3 }}>
									<TableContainer
										sx={{
											border: "1px solid #e0e0e0",
											borderRadius: "4px",
										}}
									>
										<Table size="small">
											<TableHead>
												<TableRow sx={{ bgcolor: "#f5f5f5" }}>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Número OP</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Produto Final</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Quant. Entrada</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Quant. Final</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Embalagem</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Volumes</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Data</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Lote Rastreabilidade</TableCell>
													<TableCell sx={{ fontWeight: 600, fontSize: "14px" }}>Rendimento</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												<TableRow>
													<TableCell sx={{ fontSize: "14px" }}>{dadosOP.numeroOP}</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>{dadosOP.produtoFinal}</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>
														{dadosOP.quantidadeEntrada.toLocaleString("pt-BR")}
													</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>
														{dadosOP.quantidadeFinal.toLocaleString("pt-BR")}
													</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>{dadosOP.embalagem}</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>{dadosOP.volumes}</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>{dadosOP.data}</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>{dadosOP.loteRastreabilidade}</TableCell>
													<TableCell sx={{ fontSize: "14px" }}>{dadosOP.rendimento}</TableCell>
												</TableRow>
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							)}

							{/* Etapas */}
							{etapas.length > 0 && (
								<Box sx={{ mb: 3 }}>
									<Typography
										variant="subtitle1"
										sx={{
											color: "#1a9e8f",
											fontWeight: 600,
											mb: 2,
											fontSize: "16px",
										}}
									>
										Etapas do Processo
									</Typography>
									<TableContainer
										sx={{
											border: "1px solid #e0e0e0",
											borderRadius: "4px",
										}}
									>
										<Table size="small">
											<TableHead>
												<TableRow sx={{ bgcolor: "#f5f5f5" }}>
													<TableCell sx={{ fontWeight: 600, width: temEtapaComProduto ? "6%" : "10%", fontSize: "14px" }}>
														Etapa
													</TableCell>
													<TableCell sx={{ fontWeight: 600, width: temEtapaComProduto ? "18%" : "30%", fontSize: "14px" }}>
														Nome
													</TableCell>
													<TableCell sx={{ fontWeight: 600, width: temEtapaComProduto ? "11%" : "20%", fontSize: "14px" }}>
														Quant. Entrada
													</TableCell>
													{/* Produto utilizado soma na saida total da etapa, por isso vem
														antes da saida e das perdas. */}
													{temEtapaComProduto && (
														<>
															<TableCell sx={{ fontWeight: 600, width: "25%", fontSize: "14px" }}>
																Produto Utilizado
															</TableCell>
															<TableCell sx={{ fontWeight: 600, width: "12%", fontSize: "14px" }}>
																Quant. Utilizada
															</TableCell>
														</>
													)}
													<TableCell sx={{ fontWeight: 600, width: temEtapaComProduto ? "14%" : "20%", fontSize: "14px" }}>
														Quant. Saída
													</TableCell>
													<TableCell sx={{ fontWeight: 600, width: temEtapaComProduto ? "14%" : "20%", fontSize: "14px" }}>
														Perdas
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{etapas.map((etapa, index) => (
													<TableRow key={index}>
														<TableCell sx={{ fontSize: "14px" }}>{etapa.numeroEtapa}</TableCell>
														<TableCell sx={{ fontSize: "14px" }}>{etapa.nomeEtapa}</TableCell>
														<TableCell sx={{ fontSize: "14px" }}>
															{num(etapa.quantidadeEntrada).toLocaleString("pt-BR")}
														</TableCell>
														{temEtapaComProduto && (
															<>
																<TableCell>
																	{etapa.solicitaProduto ? (
																		<CustomAutocomplete
																			size="small"
																			options={produtosDisponiveis}
																			value={
																				produtosDisponiveis.find(
																					(p) => p.id === etapa.ordemProducaoProdutoId
																				) || null
																			}
																			onChange={(newValue) =>
																				handleEtapaProdutoChange(index, newValue)
																			}
																			getOptionLabel={(option) =>
																				`${descreverOrigem(option)} | Disponível: ${num(
																					option.quantidadeDisponivel
																				).toFixed(2)}`
																			}
																			getOptionId={(option) => option.id}
																			noOptionsText="Nenhum produto de OP anterior disponível"
																			label="Produto"
																			placeholder="Buscar produto de OP anterior..."
																		/>
																	) : (
																		<Typography sx={{ fontSize: "14px", color: "#999" }}>-</Typography>
																	)}
																</TableCell>
																<TableCell>
																	{etapa.solicitaProduto ? (
																		<TextField
																			fullWidth
																			type="number"
																			value={etapa.quantidadeProduto ?? ""}
																			onChange={(e) =>
																				handleEtapaQuantidadeProdutoChange(index, e.target.value)
																			}
																			placeholder="0"
																			size="small"
																			disabled={!etapa.ordemProducaoProdutoId}
																			inputProps={{
																				style: { fontSize: "14px" },
																				min: 0,
																				max: disponivelParaEtapa(index, etapa.ordemProducaoProdutoId),
																				step: 0.01,
																			}}
																			error={
																				num(etapa.quantidadeProduto) >
																				disponivelParaEtapa(index, etapa.ordemProducaoProdutoId)
																			}
																			helperText={
																				etapa.ordemProducaoProdutoId
																					? `Máximo: ${disponivelParaEtapa(
																							index,
																							etapa.ordemProducaoProdutoId
																					  ).toFixed(2)}`
																					: ""
																			}
																		/>
																	) : (
																		<Typography sx={{ fontSize: "14px", color: "#999" }}>-</Typography>
																	)}
																</TableCell>
															</>
														)}
														<TableCell>
															{/* O maximo e a entrada: passar disso deixaria as perdas
																negativas. O produto utilizado soma sozinho na saida total. */}
															<TextField
																fullWidth
																type="number"
																value={etapa.quantidadeSaida ?? ""}
																onChange={(e) => handleEtapaSaidaChange(index, e.target.value)}
																placeholder="0"
																size="small"
																inputProps={{
																	style: { fontSize: "14px" },
																	min: 0,
																	max: num(etapa.quantidadeEntrada),
																	step: 0.01,
																}}
																error={num(etapa.quantidadeSaida) > num(etapa.quantidadeEntrada)}
																helperText={
																	num(etapa.quantidadeSaida) > num(etapa.quantidadeEntrada)
																		? `Máximo: ${num(etapa.quantidadeEntrada).toFixed(2)}`
																		: produtoDaEtapa(etapa) > 0
																		? `Saída total: ${saidaTotalDaEtapa(etapa).toLocaleString("pt-BR")}`
																		: ""
																}
															/>
														</TableCell>
														<TableCell
															sx={{
																fontSize: "14px",
																color: num(etapa.perdas) > 0 ? "#ff4d4d" : "#666",
																fontWeight: num(etapa.perdas) > 0 ? 600 : 400,
															}}
														>
															{num(etapa.perdas).toLocaleString("pt-BR")}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</Box>
							)}
						</Box>

						{/* Botão Salvar */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 2,
								px: 6,
								pb: 4,
								pt: 0,
							}}
						>
							<Button
								variant="outlined"
								startIcon={<ArrowBackIcon />}
								onClick={() => navigate("/ordem-producao")}
								disabled={saving}
								sx={{
									color: "#666",
									borderColor: "#ccc",
									"&:hover": {
										borderColor: "#999",
										bgcolor: "#f5f5f5",
									},
									textTransform: "none",
									px: 4,
									py: 1.5,
									fontSize: "15px",
									fontWeight: 500,
									borderRadius: "4px",
								}}
							>
								Voltar
							</Button>
							<Button
								variant="contained"
								onClick={handleSave}
								disabled={saving}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
										boxShadow: "0 2px 6px rgba(23, 162, 184, 0.4)",
									},
									"&:disabled": {
										bgcolor: "#ccc",
									},
									textTransform: "none",
									px: 5,
									py: 1.5,
									fontSize: "15px",
									fontWeight: 500,
									borderRadius: "4px",
									boxShadow: "none",
								}}
							>
								{saving ? "Salvando..." : isEdit ? "Atualizar" : "Cadastrar"}
							</Button>
						</Box>
					</Box>
			</Box>
		</AppLayout>
	);
}

export default OrdemProducaoCad;
